import { useState, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Settings, Users, Building2, ScrollText, Plus, Shield, Pencil, Trash2, Eye, Activity, Upload, FileText, Loader2, CheckCircle2, AlertCircle, Clock, Download, Mail, Play, Square, Send, RefreshCw, Globe, Unlink, CreditCard } from 'lucide-react';
import { Link } from 'react-router-dom';
import { WidgetAnalytics } from '../components/WidgetAnalytics';
import { IntegrationsPanel } from '../components/IntegrationsPanel';
import { OwnerActivityLog } from '../components/OwnerActivityLog';
import { SignalRejections } from '../components/SignalRejections';
import { AdminPayments } from '../components/AdminPayments';
import { RegisterExport } from '../components/RegisterExport';
import { useRef } from 'react';

export default function AdminPanel() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [showAddBiz, setShowAddBiz] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileRef = useRef(null);
  const [cronStatus, setCronStatus] = useState(null);
  const [cronLoading, setCronLoading] = useState(false);
  const [digestStatus, setDigestStatus] = useState(null);
  const [digestRecipients, setDigestRecipients] = useState('');
  const [digestSending, setDigestSending] = useState(false);
  const [digestResult, setDigestResult] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [emailLog, setEmailLog] = useState({ logs: [], stats: { sent_7d: 0, failed_7d: 0, total_all_time: 0 } });
  const [emailLogLoading, setEmailLogLoading] = useState(false);
  const [emailHealth, setEmailHealth] = useState(null);
  const [emailTestLoading, setEmailTestLoading] = useState(false);

  const loadEmailLog = async () => {
    setEmailLogLoading(true);
    try {
      const { data } = await api.get('/admin/email-log?limit=50');
      setEmailLog(data);
    } catch (e) { console.error(e); }
    finally { setEmailLogLoading(false); }
  };

  const checkEmailHealth = async () => {
    try {
      const { data } = await api.get('/auth/email-health');
      setEmailHealth(data);
    } catch (e) {
      setEmailHealth({ error: e.response?.data?.detail || 'Failed to reach diagnostic endpoint' });
    }
  };

  const sendTestEmail = async () => {
    setEmailTestLoading(true);
    try {
      const { data } = await api.post('/auth/email-test', {});
      alert(`✓ ${data.message}`);
      setTimeout(loadEmailLog, 3000);
    } catch (e) {
      alert(`✗ ${e.response?.data?.detail || 'Failed to send test email'}`);
    } finally {
      setEmailTestLoading(false);
    }
  };

  const loadUploads = async () => {
    try {
      const { data } = await api.get('/uploads');
      setUploads(data);
    } catch (e) { console.error(e); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadResult(data);
      loadUploads();
    } catch (err) {
      setUploadResult({ status: 'error', error: err.response?.data?.detail || 'Upload failed' });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin') return;
    api.get('/admin/users').then(r => setUsers(r.data)).catch(console.error);
    api.get('/admin/audit-log').then(r => setAuditLog(r.data)).catch(console.error);
    loadUploads();
    api.get('/admin/cron/status').then(r => setCronStatus(r.data)).catch(console.error);
    api.get('/admin/digest/status').then(r => setDigestStatus(r.data)).catch(console.error);
    loadEmailLog();
  }, [user]);

  const [seedBusy, setSeedBusy] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const updateCap = async (userId, raw) => {
    const value = raw === '' ? null : raw === 'unlimited' ? -1 : Number(raw);
    try {
      await api.put(`/admin/users/${userId}/query-cap`, { daily_query_cap: value });
      setUsers(prev => prev.map(u => u._id === userId ? { ...u, daily_query_cap: value } : u));
    } catch (e) {
      console.error(e);
    }
  };

  const updateRole = async (userId, role, local_authority = '') => {
    try {
      await api.put(`/admin/users/${userId}/role`, { role, local_authority });
      setUsers(prev => prev.map(u => u._id === userId ? { ...u, role, local_authority: role === 'la_user' ? local_authority : '' } : u));
    } catch (e) {
      console.error(e);
    }
  };

  const LA_OPTIONS = [
    { slug: 'stockton', name: 'Stockton-on-Tees' },
    { slug: 'middlesbrough', name: 'Middlesbrough' },
    { slug: 'redcar', name: 'Redcar and Cleveland' },
    { slug: 'hartlepool', name: 'Hartlepool' },
    { slug: 'darlington', name: 'Darlington' },
  ];

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteBusy(true); setInviteError(null);
    const form = new FormData(e.target);
    const payload = {
      email: form.get('email')?.toString().trim(),
      name: form.get('name')?.toString().trim(),
      organisation: form.get('organisation')?.toString().trim() || '',
      role: form.get('role')?.toString() || 'la_user',
      local_authority: form.get('local_authority')?.toString() || '',
    };
    try {
      const { data } = await api.post('/admin/users/invite', payload);
      setUsers(prev => [...prev, { _id: data.id, ...data }]);
      setShowInvite(false);
      e.target.reset();
    } catch (err) {
      setInviteError(err.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInviteBusy(false);
    }
  };

  const handleAddBusiness = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    const biz = {
      company_name: form.get('company_name'),
      ch_number: form.get('ch_number'),
      sic_code: form.get('sic_code'),
      primary_business: form.get('primary_business'),
      sector: form.get('sector'),
      registered_address: form.get('address'),
      postcode: form.get('postcode'),
      ch_status: form.get('ch_status'),
      website: form.get('website') === 'on',
      google_business: form.get('google_business') === 'on',
      directory_listing: form.get('directory') === 'on',
      social_media: form.get('social') === 'on',
      reviews: form.get('reviews') === 'on',
    };
    try {
      await api.post('/admin/businesses', biz);
      setShowAddBiz(false);
      e.target.reset();
    } catch (err) {
      console.error(err);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="max-w-[1200px] mx-auto px-4 lg:px-6 py-12 text-center">
        <Shield className="w-12 h-12 text-ink/15 mx-auto mb-4" />
        <h1 className="font-display text-2xl font-extrabold text-ink mb-2">Admin Access Required</h1>
        <p className="font-body text-sm text-ink/50">You need admin privileges to access this panel.</p>
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto px-4 lg:px-6 py-6 space-y-6" data-testid="admin-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-ink flex items-center gap-2">
          <Settings className="w-6 h-6 text-brand-orange" strokeWidth={1.5} /> Admin Panel
        </h1>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="add-business-btn" onClick={() => setShowAddBiz(true)} className="bg-brand-orange text-white hover:bg-brand-orange/90 font-body text-sm rounded-[4px]">
            <Plus className="w-4 h-4 mr-1" /> Add Business
          </Button>
          <Link to="/admin/verification">
            <Button variant="outline" data-testid="verification-queue-btn" className="font-body text-sm rounded-[4px]">
              <Eye className="w-4 h-4 mr-1" /> Verification Queue
            </Button>
          </Link>
          <Link to="/admin/activity">
            <Button variant="outline" data-testid="activity-tracking-btn" className="font-body text-sm rounded-[4px]">
              <Activity className="w-4 h-4 mr-1" /> Activity Tracking
            </Button>
          </Link>
        </div>
      </div>

      <Dialog open={showAddBiz} onOpenChange={setShowAddBiz}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-xl font-extrabold text-ink">Add Business</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddBusiness} className="space-y-3">
              <div><Label className="font-body text-xs text-ink/50">Company Name *</Label><Input name="company_name" required className="font-body text-sm mt-1" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="font-body text-xs text-ink/50">CH Number</Label><Input name="ch_number" className="font-body text-sm mt-1" /></div>
                <div><Label className="font-body text-xs text-ink/50">SIC Code</Label><Input name="sic_code" className="font-body text-sm mt-1" /></div>
              </div>
              <div><Label className="font-body text-xs text-ink/50">Primary Business</Label><Input name="primary_business" className="font-body text-sm mt-1" /></div>
              <div><Label className="font-body text-xs text-ink/50">Sector</Label><Input name="sector" className="font-body text-sm mt-1" /></div>
              <div><Label className="font-body text-xs text-ink/50">Address</Label><Input name="address" className="font-body text-sm mt-1" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="font-body text-xs text-ink/50">Postcode</Label><Input name="postcode" placeholder="TS18 1AA" className="font-body text-sm mt-1" /></div>
                <div>
                  <Label className="font-body text-xs text-ink/50">CH Status</Label>
                  <select name="ch_status" className="w-full mt-1 rounded-[4px] border border-ink/15 px-3 py-2 font-body text-sm">
                    <option value="Active">Active</option><option value="Dissolved">Dissolved</option><option value="Dormant">Dormant</option>
                  </select>
                </div>
              </div>
              <div className="font-body text-xs text-ink/50 uppercase tracking-wider mt-3">Digital Footprint</div>
              <div className="grid grid-cols-2 gap-2">
                {['website', 'google_business', 'directory', 'social', 'reviews'].map(f => (
                  <label key={f} className="flex items-center gap-2 font-body text-sm text-ink/70">
                    <input type="checkbox" name={f} className="rounded" />
                    {f.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </label>
                ))}
              </div>
              <Button data-testid="submit-add-business" type="submit" className="w-full bg-brand-orange text-white hover:bg-brand-orange/90 font-body text-sm rounded-[4px]">
                Add Business
              </Button>
            </form>
          </DialogContent>
        </Dialog>

      <Tabs defaultValue={new URLSearchParams(window.location.search).get('tab') || (new URLSearchParams(window.location.search).get('zoho') ? 'integrations' : 'users')}>
        <TabsList className="bg-ink/5 flex-wrap h-auto justify-start">
          <TabsTrigger value="users" className="font-body text-xs data-[state=active]:bg-white"><Users className="w-3.5 h-3.5 mr-1" /> Users</TabsTrigger>
          <TabsTrigger value="uploads" className="font-body text-xs data-[state=active]:bg-white"><Upload className="w-3.5 h-3.5 mr-1" /> Uploads</TabsTrigger>
          <TabsTrigger value="cron" className="font-body text-xs data-[state=active]:bg-white"><Clock className="w-3.5 h-3.5 mr-1" /> Cron Jobs</TabsTrigger>
          <TabsTrigger value="digest" className="font-body text-xs data-[state=active]:bg-white"><Mail className="w-3.5 h-3.5 mr-1" /> Digest Email</TabsTrigger>
          <TabsTrigger value="email-log" className="font-body text-xs data-[state=active]:bg-white" data-testid="tab-email-log"><Send className="w-3.5 h-3.5 mr-1" /> Email Deliveries</TabsTrigger>
          <TabsTrigger value="export" className="font-body text-xs data-[state=active]:bg-white"><Download className="w-3.5 h-3.5 mr-1" /> Export</TabsTrigger>
          <TabsTrigger value="audit" className="font-body text-xs data-[state=active]:bg-white"><ScrollText className="w-3.5 h-3.5 mr-1" /> Audit Log</TabsTrigger>
          <TabsTrigger value="widget" className="font-body text-xs data-[state=active]:bg-white" data-testid="tab-widget"><Globe className="w-3.5 h-3.5 mr-1" /> Landing Widget</TabsTrigger>
          <TabsTrigger value="integrations" className="font-body text-xs data-[state=active]:bg-white" data-testid="tab-integrations"><Send className="w-3.5 h-3.5 mr-1" /> Integrations</TabsTrigger>
          <TabsTrigger value="owners" className="font-body text-xs data-[state=active]:bg-white" data-testid="tab-owners"><ScrollText className="w-3.5 h-3.5 mr-1" /> Owner Activity</TabsTrigger>
          <TabsTrigger value="rejections" className="font-body text-xs data-[state=active]:bg-white" data-testid="tab-rejections"><Unlink className="w-3.5 h-3.5 mr-1" /> Disconnected Links</TabsTrigger>
          <TabsTrigger value="payments" className="font-body text-xs data-[state=active]:bg-white" data-testid="tab-payments"><CreditCard className="w-3.5 h-3.5 mr-1" /> Payments</TabsTrigger>
        </TabsList>

        <TabsContent value="payments" className="mt-4">
          <AdminPayments />
        </TabsContent>

        <TabsContent value="rejections" className="mt-4">
          <SignalRejections />
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <p className="font-body text-sm text-ink/60">Manage user roles &amp; LA scoping. Invite council staff limited to their own LA.</p>
            <Dialog open={showInvite} onOpenChange={setShowInvite}>
              <DialogTrigger asChild>
                <Button data-testid="invite-user-btn" className="bg-brand-orange text-white hover:bg-brand-orange/90 font-body text-xs rounded-[4px]">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Invite user
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-display text-lg">Invite council user</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleInvite} className="space-y-3" data-testid="invite-user-form">
                  <div>
                    <Label htmlFor="invite-name" className="font-body text-xs">Full name</Label>
                    <Input id="invite-name" name="name" required className="font-body text-sm" data-testid="invite-name" />
                  </div>
                  <div>
                    <Label htmlFor="invite-email" className="font-body text-xs">Email</Label>
                    <Input id="invite-email" name="email" type="email" required className="font-body text-sm" data-testid="invite-email" />
                  </div>
                  <div>
                    <Label htmlFor="invite-org" className="font-body text-xs">Organisation (optional)</Label>
                    <Input id="invite-org" name="organisation" className="font-body text-sm" data-testid="invite-org" />
                  </div>
                  <div>
                    <Label htmlFor="invite-role" className="font-body text-xs">Role</Label>
                    <select name="role" id="invite-role" defaultValue="la_user" className="w-full border border-ink/15 rounded-[4px] px-3 py-2 font-body text-sm" data-testid="invite-role">
                      <option value="la_user">LA User (scoped)</option>
                      <option value="institutional">Institutional (all TVCA)</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="invite-la" className="font-body text-xs">Local Authority (required for LA User)</Label>
                    <select name="local_authority" id="invite-la" defaultValue="" className="w-full border border-ink/15 rounded-[4px] px-3 py-2 font-body text-sm" data-testid="invite-la">
                      <option value="">— None (unrestricted) —</option>
                      {LA_OPTIONS.map(la => <option key={la.slug} value={la.slug}>{la.name}</option>)}
                    </select>
                  </div>
                  {inviteError && <p className="font-body text-xs text-status-red" data-testid="invite-error">{inviteError}</p>}
                  <Button type="submit" disabled={inviteBusy} className="w-full bg-brand-orange text-white hover:bg-brand-orange/90 font-body text-sm rounded-[4px]" data-testid="invite-submit">
                    {inviteBusy ? 'Sending…' : 'Send invitation'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="solid-card overflow-hidden">
            <table className="w-full">
              <thead className="bg-ink/3">
                <tr>
                  <th className="text-left p-3 font-body text-xs uppercase tracking-wider text-ink/50">Name</th>
                  <th className="text-left p-3 font-body text-xs uppercase tracking-wider text-ink/50">Email</th>
                  <th className="text-left p-3 font-body text-xs uppercase tracking-wider text-ink/50">Organisation</th>
                  <th className="text-left p-3 font-body text-xs uppercase tracking-wider text-ink/50">Role</th>
                  <th className="text-left p-3 font-body text-xs uppercase tracking-wider text-ink/50">LA Scope</th>
                  <th className="text-left p-3 font-body text-xs uppercase tracking-wider text-ink/50">Daily Query Cap</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u._id} className="border-t border-ink/5" data-testid={`user-row-${u._id}`}>
                    <td className="p-3 font-body text-sm text-ink">{u.name}</td>
                    <td className="p-3 font-body text-xs text-ink/60">{u.email}</td>
                    <td className="p-3 font-body text-xs text-ink/60">{u.organisation}</td>
                    <td className="p-3">
                      <Select value={u.role || 'public'} onValueChange={v => updateRole(u._id, v, u.local_authority)}>
                        <SelectTrigger className="w-[130px] font-body text-xs h-8" data-testid={`role-select-${u._id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="public">Public</SelectItem>
                          <SelectItem value="institutional">Institutional</SelectItem>
                          <SelectItem value="la_user">LA User</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-3">
                      {u.role === 'la_user' ? (
                        <Select value={u.local_authority || ''} onValueChange={v => updateRole(u._id, 'la_user', v)}>
                          <SelectTrigger className="w-[160px] font-body text-xs h-8" data-testid={`la-select-${u._id}`}><SelectValue placeholder="Pick LA…" /></SelectTrigger>
                          <SelectContent>
                            {LA_OPTIONS.map(la => <SelectItem key={la.slug} value={la.slug}>{la.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className={`text-xs font-body px-2 py-0.5 rounded-[4px] ${
                          u.role === 'admin' ? 'bg-brand-orange/10 text-brand-orange' :
                          u.role === 'institutional' ? 'bg-info-blue/10 text-info-blue' :
                          'bg-ink/5 text-ink/40'
                        }`}>{u.role || 'public'}</span>
                      )}
                    </td>
                    <td className="p-3">
                      <Select value={u.daily_query_cap === undefined || u.daily_query_cap === null ? '' : u.daily_query_cap === -1 ? 'unlimited' : String(u.daily_query_cap)} onValueChange={v => updateCap(u._id, v === 'default' ? '' : v)}>
                        <SelectTrigger className="w-[150px] font-body text-xs h-8" data-testid={`cap-select-${u._id}`}>
                          <SelectValue placeholder={`Plan default (${u.role === 'admin' || u.role === 'la_user' || u.role === 'institutional' ? '∞' : { pro: 50, premium: 200, enterprise: '∞' }[u.plan_id] ?? 5}/day)`} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Plan default</SelectItem>
                          <SelectItem value="5">5 / day (individual)</SelectItem>
                          <SelectItem value="50">50 / day (Standard)</SelectItem>
                          <SelectItem value="200">200 / day (Premium)</SelectItem>
                          <SelectItem value="500">500 / day (raised)</SelectItem>
                          <SelectItem value="1000">1,000 / day</SelectItem>
                          <SelectItem value="unlimited">Unlimited (LA / BSO)</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className="block font-body text-[10px] text-ink/40 mt-1" data-testid={`cap-used-${u._id}`}>{u.queries_date === new Date().toISOString().slice(0, 10) ? (u.queries_used_today || 0) : 0} used today</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="uploads" className="mt-4">
          <div className="space-y-4">
            <div>
              <p className="font-body text-sm text-ink/50 max-w-2xl leading-relaxed mb-3">
                Upload business data files (CSV, Excel) for bulk import, or PDF reports for AI analysis.
              </p>
              <div className="solid-card p-8 text-center border-2 border-dashed border-ink/15 hover:border-brand-orange/30 transition-all cursor-pointer"
                onClick={() => fileRef.current?.click()}
                data-testid="admin-upload-dropzone"
              >
                <input ref={fileRef} type="file" accept=".csv,.pdf,.xlsx,.xls" onChange={handleUpload} className="hidden" data-testid="admin-upload-file-input" />
                {uploading ? (
                  <div>
                    <Loader2 className="w-10 h-10 text-brand-orange mx-auto mb-3 animate-spin" />
                    <p className="font-body text-sm text-ink/60">Processing file...</p>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-10 h-10 text-ink/20 mx-auto mb-3" strokeWidth={1.5} />
                    <p className="font-body text-sm text-ink/60">Click to upload or drag & drop</p>
                    <p className="font-body text-xs text-ink/30 mt-1">Supported: CSV, Excel (.xlsx), PDF</p>
                  </div>
                )}
              </div>
            </div>

            {uploadResult && (
              <div className={`solid-card p-4 border-l-4 animate-fade-in ${uploadResult.status === 'error' ? 'border-l-status-red' : 'border-l-status-green'}`}>
                <div className="flex items-start gap-3">
                  {uploadResult.status === 'error' ? (
                    <AlertCircle className="w-5 h-5 text-status-red shrink-0" strokeWidth={1.5} />
                  ) : (
                    <CheckCircle2 className="w-5 h-5 text-status-green shrink-0" strokeWidth={1.5} />
                  )}
                  <div>
                    <p className="font-body text-sm text-ink">{uploadResult.status === 'error' ? 'Upload Failed' : `${uploadResult.original_filename} uploaded successfully`}</p>
                    {uploadResult.records_imported > 0 && <p className="font-body text-xs text-status-green mt-1">{uploadResult.records_imported} business records imported</p>}
                    {uploadResult.error && <p className="font-body text-xs text-status-red mt-1">{uploadResult.error}</p>}
                  </div>
                </div>
              </div>
            )}

            <div>
              <h3 className="font-display text-lg font-extrabold text-ink mb-3">Upload History</h3>
              {uploads.length === 0 ? (
                <div className="solid-card p-6 text-center">
                  <FileText className="w-8 h-8 text-ink/15 mx-auto mb-2" />
                  <p className="font-body text-sm text-ink/40">No files uploaded yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {uploads.map(f => (
                    <div key={f.id} className="solid-card p-3 flex items-center justify-between" data-testid={`admin-upload-item-${f.id}`}>
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-info-blue" strokeWidth={1.5} />
                        <div>
                          <span className="font-body text-sm text-ink">{f.original_filename}</span>
                          <span className="font-body text-xs text-ink/40 block">
                            {new Date(f.created_at).toLocaleDateString()} · {f.file_type?.toUpperCase()} · {f.status}
                            {f.records_imported > 0 && ` · ${f.records_imported} records`}
                          </span>
                        </div>
                      </div>
                      <span className={`text-xs font-body px-2 py-0.5 rounded-[4px] ${
                        f.status === 'processed' ? 'bg-status-green/10 text-status-green' :
                        f.status === 'error' ? 'bg-status-red/10 text-status-red' :
                        'bg-status-amber/10 text-status-amber'
                      }`}>{f.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Cron Jobs Tab */}
        <TabsContent value="cron" className="mt-4">
          <div className="space-y-4">
            <div className="solid-card p-5">
              <h3 className="font-display text-lg font-extrabold text-ink mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-brand-orange" strokeWidth={1.5} /> Scheduled Re-verification
              </h3>
              <p className="font-body text-sm text-ink/50 mb-4">Automatically re-scores businesses whose verification is older than 7 days using the probabilistic estimation engine.</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="p-3 rounded-[4px] bg-ink/3 text-center">
                  <span className="font-body text-[10px] uppercase tracking-[0.1em] text-ink/40 block">Status</span>
                  <span className={`font-display text-lg font-extrabold ${cronStatus?.running ? 'text-status-green' : 'text-ink/30'}`}>{cronStatus?.running ? 'Running' : 'Stopped'}</span>
                </div>
                <div className="p-3 rounded-[4px] bg-ink/3 text-center">
                  <span className="font-body text-[10px] uppercase tracking-[0.1em] text-ink/40 block">Interval</span>
                  <span className="font-display text-lg font-extrabold text-ink">{cronStatus?.interval_hours || 24}h</span>
                </div>
                <div className="p-3 rounded-[4px] bg-ink/3 text-center">
                  <span className="font-body text-[10px] uppercase tracking-[0.1em] text-ink/40 block">Last Run</span>
                  <span className="font-body text-xs text-ink/60">{cronStatus?.last_run ? new Date(cronStatus.last_run).toLocaleString() : 'Never'}</span>
                </div>
                <div className="p-3 rounded-[4px] bg-ink/3 text-center">
                  <span className="font-body text-[10px] uppercase tracking-[0.1em] text-ink/40 block">Last Result</span>
                  <span className="font-body text-xs text-ink/60">{cronStatus?.last_result?.re_verified != null ? `${cronStatus.last_result.re_verified} re-verified` : '--'}</span>
                </div>
              </div>
              <div className="flex gap-2">
                {!cronStatus?.running ? (
                  <Button data-testid="cron-start" disabled={cronLoading} onClick={async () => {
                    setCronLoading(true);
                    try {
                      const { data } = await api.post('/admin/cron/start', { interval_hours: 24 });
                      setCronStatus(data.status);
                    } catch (e) { console.error(e); }
                    setCronLoading(false);
                  }} className="bg-status-green text-white hover:bg-status-green/90 font-body text-sm rounded-[4px]">
                    <Play className="w-4 h-4 mr-1" /> Start Cron (24h)
                  </Button>
                ) : (
                  <Button data-testid="cron-stop" disabled={cronLoading} onClick={async () => {
                    setCronLoading(true);
                    try {
                      const { data } = await api.post('/admin/cron/stop');
                      setCronStatus(data.status);
                    } catch (e) { console.error(e); }
                    setCronLoading(false);
                  }} className="bg-status-red text-white hover:bg-status-red/90 font-body text-sm rounded-[4px]">
                    <Square className="w-4 h-4 mr-1" /> Stop Cron
                  </Button>
                )}
                <Button data-testid="cron-run-now" variant="outline" disabled={cronLoading} onClick={async () => {
                  setCronLoading(true);
                  try {
                    const { data } = await api.post('/admin/cron/run-now');
                    setCronStatus(prev => ({ ...prev, last_run: new Date().toISOString(), last_result: data.result }));
                  } catch (e) { console.error(e); }
                  setCronLoading(false);
                }} className="font-body text-sm rounded-[4px]">
                  <RefreshCw className={`w-4 h-4 mr-1 ${cronLoading ? 'animate-spin' : ''}`} /> Run Now
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Digest Email Tab */}
        <TabsContent value="digest" className="mt-4">
          <div className="space-y-4">
            <div className="solid-card p-5">
              <h3 className="font-display text-lg font-extrabold text-ink mb-3 flex items-center gap-2">
                <Mail className="w-4 h-4 text-brand-orange" strokeWidth={1.5} /> Weekly Digest Email
              </h3>
              <p className="font-body text-sm text-ink/50 mb-4">Send a weekly analytics summary to stakeholders. Includes borough overview, district breakdown, and recent activity.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                <div className="p-3 rounded-[4px] bg-ink/3 text-center">
                  <span className="font-body text-[10px] uppercase tracking-[0.1em] text-ink/40 block">Schedule</span>
                  <span className={`font-display text-lg font-extrabold ${digestStatus?.running ? 'text-status-green' : 'text-ink/30'}`}>{digestStatus?.running ? 'Active' : 'Inactive'}</span>
                </div>
                <div className="p-3 rounded-[4px] bg-ink/3 text-center">
                  <span className="font-body text-[10px] uppercase tracking-[0.1em] text-ink/40 block">Last Sent</span>
                  <span className="font-body text-xs text-ink/60">{digestStatus?.last_sent ? new Date(digestStatus.last_sent).toLocaleString() : 'Never'}</span>
                </div>
                <div className="p-3 rounded-[4px] bg-ink/3 text-center">
                  <span className="font-body text-[10px] uppercase tracking-[0.1em] text-ink/40 block">Recipients</span>
                  <span className="font-display text-lg font-extrabold text-ink">{digestStatus?.recipients?.length || 0}</span>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="font-body text-xs text-ink/50">Recipient Emails (comma-separated)</Label>
                  <Input data-testid="digest-recipients" value={digestRecipients} onChange={e => setDigestRecipients(e.target.value)} placeholder="admin@infralytics.co, analyst@stockton.gov.uk" className="font-body text-sm mt-1" />
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button data-testid="digest-send-now" disabled={digestSending || !digestRecipients.trim()} onClick={async () => {
                    setDigestSending(true); setDigestResult(null);
                    try {
                      const recipients = digestRecipients.split(',').map(e => e.trim()).filter(Boolean);
                      const { data } = await api.post('/admin/digest/send-now', { recipients });
                      setDigestResult(data);
                      api.get('/admin/digest/status').then(r => setDigestStatus(r.data));
                    } catch (e) { setDigestResult({ message: 'Failed', error: e.response?.data?.detail || e.message }); }
                    setDigestSending(false);
                  }} className="bg-brand-orange text-white hover:bg-brand-orange/90 font-body text-sm rounded-[4px]">
                    {digestSending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send Now
                  </Button>
                  <Button data-testid="digest-schedule" variant="outline" disabled={digestSending || !digestRecipients.trim()} onClick={async () => {
                    setDigestSending(true);
                    try {
                      const recipients = digestRecipients.split(',').map(e => e.trim()).filter(Boolean);
                      const { data } = await api.post('/admin/digest/schedule', { recipients });
                      setDigestResult(data);
                      setDigestStatus(data.status);
                    } catch (e) { console.error(e); }
                    setDigestSending(false);
                  }} className="font-body text-sm rounded-[4px]">
                    <Clock className="w-4 h-4 mr-1" /> Schedule Weekly
                  </Button>
                  {digestStatus?.running && (
                    <Button variant="outline" onClick={async () => {
                      const { data } = await api.post('/admin/digest/stop');
                      setDigestStatus(data.status);
                    }} className="font-body text-sm rounded-[4px] text-status-red border-status-red/30">
                      <Square className="w-4 h-4 mr-1" /> Stop Schedule
                    </Button>
                  )}
                </div>
              </div>
              {digestResult && (
                <div className={`mt-3 solid-card p-3 border-l-4 ${digestResult.error ? 'border-l-status-red' : 'border-l-status-green'}`}>
                  <p className="font-body text-sm text-ink">{digestResult.message}</p>
                  {digestResult.results?.map((r, i) => (
                    <p key={i} className={`font-body text-xs mt-1 ${r.status === 'sent' ? 'text-status-green' : 'text-status-red'}`}>
                      {r.email}: {r.status} {r.id ? `(${r.id.slice(0,8)}...)` : ''} {r.error || ''}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Export Tab */}
        <TabsContent value="export" className="mt-4 space-y-4">
          <RegisterExport />
          <div className="solid-card p-5 border-l-4 border-l-brand-orange" data-testid="seed-load-card">
            <h3 className="font-display text-lg font-extrabold text-ink mb-1 flex items-center gap-2"><Upload className="w-4 h-4 text-brand-orange" strokeWidth={1.5} /> Production data — load bundled register</h3>
            <p className="font-body text-xs text-ink/60 mb-3">Loads the register, geocodes and metrics shipped with this build (backend/data/seed, 37,769 businesses) into <b>this</b> database. Idempotent — safe to run again. Use after the first deploy to app.adoptic.io, or run <code className="bg-ink/5 px-1 rounded">PROD_MONGO_URL=… python3 scripts/migrate_to_production.py</code> from preview.</p>
            <div className="flex items-center gap-3 flex-wrap">
              <Button onClick={async () => { setSeedBusy(true); setSeedResult(null); try { const r = await api.post('/admin/seed/load', {}); setSeedResult(r.data); } catch (e) { setSeedResult({ error: e.response?.data?.detail || 'Failed' }); } finally { setSeedBusy(false); } }} disabled={seedBusy} className="rounded-full bg-brand-orange hover:bg-highlight-orange font-body" data-testid="seed-load-btn">{seedBusy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Loading…</> : 'Load bundled data into this database'}</Button>
              {seedResult && <span className="font-body text-xs text-ink/70" data-testid="seed-load-result">{seedResult.error ? `Error: ${seedResult.error}` : `Loaded ${Object.values(seedResult.loaded || {}).reduce((a, b) => a + b, 0).toLocaleString()} docs · businesses now ${seedResult.businesses?.toLocaleString()}`}</span>}
            </div>
          </div>
          <div className="solid-card p-5">
            <h3 className="font-display text-lg font-extrabold text-ink mb-3 flex items-center gap-2">
              <Download className="w-4 h-4 text-brand-orange" strokeWidth={1.5} /> Export Analytics
            </h3>
            <p className="font-body text-sm text-ink/50 mb-4">Download the full register data as a multi-sheet Excel workbook including businesses, district summary, sector analysis, tier distribution, and verification stats.</p>
            <div className="flex gap-3 flex-wrap">
              <Button data-testid="export-excel" disabled={exportLoading} onClick={async () => {
                setExportLoading(true);
                try {
                  const resp = await api.get('/export/excel', { responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([resp.data]));
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `stockton_register_${new Date().toISOString().slice(0,10)}.xlsx`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                } catch (e) { console.error(e); }
                setExportLoading(false);
              }} className="bg-brand-orange text-white hover:bg-brand-orange/90 font-body text-sm rounded-[4px]">
                {exportLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} Download Excel (Full Register)
              </Button>
              <Button variant="outline" disabled={exportLoading} onClick={async () => {
                setExportLoading(true);
                try {
                  const resp = await api.post('/export/csv', {}, { responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([resp.data]));
                  const a = document.createElement('a');
                  a.href = url; a.download = 'stockton_register_export.csv'; a.click();
                  window.URL.revokeObjectURL(url);
                } catch (e) { console.error(e); }
                setExportLoading(false);
              }} className="font-body text-sm rounded-[4px]">
                <Download className="w-4 h-4 mr-1" /> Download CSV
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="owners" className="mt-4">
          <OwnerActivityLog />
        </TabsContent>

        <TabsContent value="integrations" className="mt-4">
          <IntegrationsPanel />
        </TabsContent>

        <TabsContent value="widget" className="mt-4">
          <WidgetAnalytics />
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <div className="solid-card p-4">
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {auditLog.length === 0 ? (
                <p className="font-body text-sm text-ink/40 text-center py-6">No audit events recorded yet</p>
              ) : auditLog.map((log, i) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded-[4px] bg-ink/3">
                  <div className="w-2 h-2 rounded-full bg-info-blue shrink-0" />
                  <div className="flex-1">
                    <span className="font-body text-xs text-ink">{log.event}</span>
                    {log.target_user && <span className="font-body text-xs text-ink/40 ml-2">User: {log.target_user}</span>}
                    {log.business_id && <span className="font-body text-xs text-ink/40 ml-2">Business: {log.business_id}</span>}
                  </div>
                  <span className="font-body text-xs text-ink/30 shrink-0">{new Date(log.timestamp).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Email Deliveries Tab */}
        <TabsContent value="email-log" className="mt-4">
          <div className="solid-card p-4" data-testid="email-log-panel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-display text-lg font-bold text-ink flex items-center gap-2"><Send className="w-4 h-4 text-brand-orange" strokeWidth={1.5} /> Email Deliveries</h3>
                <p className="font-body text-xs text-ink/50 mt-0.5">Last 50 Resend attempts — welcome, reset, digest, admin notifications</p>
              </div>
              <Button size="sm" variant="outline" onClick={loadEmailLog} disabled={emailLogLoading} className="font-body text-xs" data-testid="email-log-refresh">
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${emailLogLoading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>

            {/* Diagnostics row */}
            <div className="mb-4 p-3 rounded-[4px] bg-info-blue/5 border border-info-blue/20 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[300px]">
                <span className="font-body text-[10px] uppercase tracking-wider text-info-blue font-medium block mb-1">Production Diagnostics</span>
                {emailHealth ? (
                  emailHealth.error ? (
                    <span className="font-body text-xs text-error-red">{emailHealth.error}</span>
                  ) : (
                    <div className="font-mono text-[11px] text-ink/70 space-y-0.5">
                      <div><span className={emailHealth.resend_api_key_set ? 'text-success-green' : 'text-error-red'}>{emailHealth.resend_api_key_set ? '✓' : '✗'}</span> RESEND_API_KEY: {emailHealth.resend_api_key_prefix}</div>
                      <div><span className={emailHealth.resend_reachable ? 'text-success-green' : 'text-error-red'}>{emailHealth.resend_reachable ? '✓' : '✗'}</span> Resend API reachable{emailHealth.resend_error ? `: ${emailHealth.resend_error}` : ''}</div>
                      <div><span className="text-success-green">✓</span> Sender: {emailHealth.sender_email}</div>
                      {emailHealth.verified_domains?.map((d, i) => (
                        <div key={i}><span className={d.status === 'verified' ? 'text-success-green' : 'text-error-red'}>{d.status === 'verified' ? '✓' : '✗'}</span> Domain: {d.name} [{d.status}] {d.region}</div>
                      ))}
                    </div>
                  )
                ) : (
                  <span className="font-body text-xs text-ink/40">Click "Check Health" to diagnose email pipeline</span>
                )}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={checkEmailHealth} className="font-body text-xs" data-testid="email-health-check">
                  <Shield className="w-3.5 h-3.5 mr-1" /> Check Health
                </Button>
                <Button size="sm" onClick={sendTestEmail} disabled={emailTestLoading} className="font-body text-xs bg-brand-orange hover:bg-brand-orange/90 text-white" data-testid="email-test-send">
                  {emailTestLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} Send Test Email
                </Button>
              </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="p-3 rounded-[4px] bg-success-green/8 border border-success-green/20" data-testid="stat-sent-7d">
                <span className="font-body text-[10px] uppercase tracking-wider text-success-green/80 font-medium">Sent (7d)</span>
                <span className="font-display text-2xl font-extrabold text-success-green block mt-0.5">{emailLog.stats.sent_7d}</span>
              </div>
              <div className="p-3 rounded-[4px] bg-error-red/8 border border-error-red/20" data-testid="stat-failed-7d">
                <span className="font-body text-[10px] uppercase tracking-wider text-error-red/80 font-medium">Failed (7d)</span>
                <span className="font-display text-2xl font-extrabold text-error-red block mt-0.5">{emailLog.stats.failed_7d}</span>
              </div>
              <div className="p-3 rounded-[4px] bg-ink/5 border border-ink/10" data-testid="stat-total">
                <span className="font-body text-[10px] uppercase tracking-wider text-ink/50 font-medium">Total (all time)</span>
                <span className="font-display text-2xl font-extrabold text-ink block mt-0.5">{emailLog.stats.total_all_time}</span>
              </div>
            </div>

            {/* Log table */}
            <div className="max-h-[500px] overflow-y-auto border border-ink/10 rounded-[4px]">
              {emailLog.logs.length === 0 ? (
                <p className="font-body text-sm text-ink/40 text-center py-8">No email activity yet</p>
              ) : (
                <table className="w-full text-xs" data-testid="email-log-table">
                  <thead className="bg-ink/5 sticky top-0 z-10">
                    <tr>
                      <th className="text-left font-body text-[10px] uppercase tracking-wider text-ink/50 font-medium px-3 py-2">Status</th>
                      <th className="text-left font-body text-[10px] uppercase tracking-wider text-ink/50 font-medium px-3 py-2">Time</th>
                      <th className="text-left font-body text-[10px] uppercase tracking-wider text-ink/50 font-medium px-3 py-2">To</th>
                      <th className="text-left font-body text-[10px] uppercase tracking-wider text-ink/50 font-medium px-3 py-2">Subject</th>
                      <th className="text-left font-body text-[10px] uppercase tracking-wider text-ink/50 font-medium px-3 py-2">Resend ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailLog.logs.map((log, i) => {
                      const isSent = log.status === 'sent';
                      const pillBg = isSent ? 'bg-success-green/15 text-success-green' : 'bg-error-red/15 text-error-red';
                      return (
                        <tr key={i} className="border-t border-ink/5 hover:bg-ink/3" data-testid={`email-log-row-${i}`}>
                          <td className="px-3 py-2">
                            <span className={`inline-block px-1.5 py-0.5 rounded-[3px] text-[9px] uppercase tracking-wider font-medium ${pillBg}`}>
                              {log.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 font-body text-[11px] text-ink/60 whitespace-nowrap">
                            {new Date(log.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="px-3 py-2 font-body text-[11px] text-ink max-w-[200px] truncate" title={log.to}>{log.to}</td>
                          <td className="px-3 py-2 font-body text-[11px] text-ink/70 max-w-[280px] truncate" title={log.subject}>{log.subject}</td>
                          <td className="px-3 py-2 font-mono text-[10px] text-ink/40">
                            {log.resend_id ? (
                              <span title={log.resend_id}>{log.resend_id.substring(0, 8)}…</span>
                            ) : log.error ? (
                              <span className="text-error-red" title={log.error}>error</span>
                            ) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </TabsContent>

      </Tabs>
    </div>
  );
}
